import { useCallback, useState } from "react"
import styled from "styled-components"

import { useAppDispatch, useAppSelector } from "reducers/store"
import ApiApiMappings from "api/ApiApiMappings"
import { showToast } from "reducers/toast/toast-actions"
import { useNavigate } from "react-router-dom"
import { useMutation, useQueryClient } from "react-query"
import { API_MAPPING, changeModalState } from "reducers/api-mappings/api-mapping-actions"

import Button from "components/EvtKit/button/Button"
import Modal from "components/EvtKit/modal/Modal"
import BodyText from "components/Typography/BodyText"
import Heading from "components/Typography/Heading"
import AttentionIconError from "components/icons/AttentionIconError"
import useHandleApiError from "hooks/useHandleApiErrors"

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const ButtonContent = styled.div`
  display: flex;
  width: 220px;
`

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const ButtonContainer = styled.div`
  justify-content: flex-end;
  display: flex;
  flex-direction: row;
  width: 100%;
  gap: 20px;
`

const KeepConected = styled.input`
  appearance: none;
  width: 24px;
  height: 24px;
  border: 1px solid ${(props) => props.theme.palette.error};
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  cursor: pointer;

  &:before {
    transition: 0.3s ease-in-out;
    content: "";
    position: absolute;
    width: 8px;
    height: 8px;
    opacity: 0;
    border-bottom: 1px solid ${(props) => props.theme.palette.white};
    border-left: 1px solid ${(props) => props.theme.palette.white};
    transform: rotate(-45deg);
    top: 4px;
  }

  &:checked:before {
    opacity: 1;
  }

  &:checked {
    background-color: ${(props) => props.theme.palette.error};
    border: 1px solid ${(props) => props.theme.palette.error};
  }
`

const ContainerInputAndTextConfirmation = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
`

const Label = styled.label`
  cursor: pointer;
`

interface DeleteEndpointsProps {
  endpointData: App.ApiMapping | undefined
}

export const DeleteEndpoints = (props: DeleteEndpointsProps) => {
  const { endpointData } = props
  const appDispatch = useAppDispatch()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { type, state } = useAppSelector((state) => state.apiMapping.modal)

  const [consentCheckbox, setConsentCheckbox] = useState<boolean>(false)

  const handleModalState = useCallback(() => {
    appDispatch(changeModalState(false, ""))
  }, [])

  const { getErrorMessageAndShowErrorToast } = useHandleApiError()

  const deleteEndpoint = useMutation({
    mutationFn: async (item: string) => {
      return await new ApiApiMappings().delete(item)
    },
    onSuccess: () => {
      appDispatch(changeModalState(false, ""))
      queryClient.invalidateQueries(API_MAPPING)
      navigate("/api-mappings")
      appDispatch(showToast("Endpoint deletado com sucesso", "success"))
    },
    onError: (error) => {
      getErrorMessageAndShowErrorToast(error, "Erro ao deletar endpoint")
    }
  })

  const handleDelete = useCallback(async (item) => {
    deleteEndpoint.mutate(item)
  }, [])

  const updateConsentCheckbox = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setConsentCheckbox(e.target.checked)
  }, [])

  return (
    <Modal openModal={state && type === "delete"} closeModal={handleModalState} size="md">
      <ModalContent>
        <IconContainer>
          <AttentionIconError />
          <Heading color="error" variant="h4">
            Deletar Endpoint
          </Heading>
        </IconContainer>
        <BodyText color="error" size="medium">
          Esteja ciente de que a exclusão do endpoint <b>{endpointData?.name}</b> é uma ação irreversível.
        </BodyText>
        <ContainerInputAndTextConfirmation>
          <KeepConected
            onChange={updateConsentCheckbox}
            type="checkbox"
            id="checkbox-confirmation-delete"
            required
            data-testId="checkbox"
          />
          <Label htmlFor="checkbox-confirmation-delete">
            <BodyText color="error" size="medium">
              Tenho certeza de que desejo prosseguir com a exclusão deste Endpoint.
            </BodyText>
          </Label>
        </ContainerInputAndTextConfirmation>
        <ButtonContainer>
          <ButtonContent>
            <Button
              className={`fill-width secondary-error`}
              onClick={() => handleDelete(endpointData?._id)}
              loading={deleteEndpoint.isLoading}
              disabled={!consentCheckbox || deleteEndpoint.isLoading}
            >
              Excluir endpoint
            </Button>
          </ButtonContent>
          <ButtonContent>
            <Button label="Cancelar" className={`fill-width attention-error`} onClick={handleModalState} />
          </ButtonContent>
        </ButtonContainer>
      </ModalContent>
    </Modal>
  )
}
